import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Card, Skeleton, useToast } from '../../../design-system';
import '../../styles/Attendance.css';

// The whole team's month: one row per person, one cell per day.
//
// A grid rather than the WorkCalendar used elsewhere — that draws ONE month for
// ONE subject, and forty of them stacked answers no question. What a manager
// actually asks is "who is off on the 14th", which is a column, and "how has
// Ravi's month gone", which is a row. Both are one glance here.
//
// The tones are the same CSS classes the per-person calendar uses, so a colour
// cannot mean two things in the same product.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const currentYm = () => new Date().toISOString().slice(0, 7);
const todayKey = () => new Date().toISOString().slice(0, 10);

const STATUS_WORD = {
  present: 'Present', half_day: 'Half day', absent: 'Absent',
  leave: 'On leave', leave_pending: 'Leave — awaiting approval',
  holiday: 'Holiday', week_off: 'Weekly off', upcoming: '—',
};

// Server status → the CSS tone class. A map rather than interpolating the
// status straight into the class name: the server says `week_off` and
// `half_day`, the stylesheet says `is-off` and `is-half`, and interpolating
// silently produced `is-week_off` — a class nothing defines, so every Sunday
// rendered as an unstyled gap in the grid.
const TONE = {
  present: 'is-present',
  half_day: 'is-half',
  absent: 'is-absent',
  leave: 'is-leave',
  leave_pending: 'is-leave is-pending',
  holiday: 'is-holiday',
  week_off: 'is-off',
  upcoming: 'is-upcoming',
};

// 'YYYY-MM-DD' → weekday index, parsed as UTC so it can't drift a day west of
// Greenwich. Same rule the rest of the module follows.
const dowOf = (k) => new Date(`${k}T00:00:00Z`).getUTCDay();
const dayNum = (k) => Number(k.slice(8, 10));

function TeamCalendar() {
  const toast = useToast();
  const [ym, setYm] = useState(currentYm);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get('/api/attendance/team/calendar', { params: { month: ym } })
      .then((r) => setData(r.data))
      .catch((e) => toast.danger(e.response?.data?.error || 'Could not load the team calendar'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ym]);
  useEffect(load, [load]);

  const step = (delta) => setYm((v) => {
    const [y, m] = v.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    return d.toISOString().slice(0, 7);
  });

  const holidayName = useMemo(
    () => Object.fromEntries((data?.holidays || []).map((h) => [h.date, h.name])),
    [data]
  );

  const describe = (row, d) => {
    const bits = [row.name, STATUS_WORD[d.status] || d.status];
    if (d.leaveType) {
      bits[1] = `${d.leaveType}${d.leaveStatus === 'pending' ? ' — awaiting approval' : ''}`;
      if (d.leavePortion && d.leavePortion !== 'full') {
        bits.push(d.leavePortion === 'first_half' ? 'first half' : 'second half');
      }
    }
    if (d.status === 'holiday' && holidayName[d.dateKey]) bits[1] = holidayName[d.dateKey];
    if (d.workedMinutes) bits.push(`${Math.floor(d.workedMinutes / 60)}h ${String(d.workedMinutes % 60).padStart(2, '0')}m`);
    return `${bits.join(' · ')} — ${d.dateKey}`;
  };

  const [y, m] = ym.split('-').map(Number);
  const keys = data?.dayKeys || [];
  const tk = todayKey();

  return (
    <Card>
      <div className="att-head">
        <div>
          <h3 className="att-head-title">Team calendar</h3>
          <p className="att-head-sub">
            Everyone&apos;s month. Hover any square for who, what and how long.
          </p>
        </div>
        <div className="att-head-actions">
          <button type="button" className="att-cal-nav" onClick={() => step(-1)} aria-label="Previous month">‹</button>
          <span className="att-cal-title">{MONTHS[m - 1]} {y}</span>
          <button type="button" className="att-cal-nav" onClick={() => step(1)} aria-label="Next month">›</button>
          <button type="button" className="att-cal-today" onClick={() => setYm(currentYm())}>Today</button>
        </div>
      </div>

      {loading || !data ? <Skeleton.Text lines={8} /> : data.rows.length === 0 ? (
        <p className="att-empty">No employees to show.</p>
      ) : (
        <div className="att-team-cal">
          <table className="att-team-grid">
            <thead>
              <tr>
                <th className="att-team-name" />
                {keys.map((k) => (
                  <th key={k} className="att-team-dow">{DOW[dowOf(k)]}</th>
                ))}
              </tr>
              <tr>
                <th className="att-team-name" />
                {keys.map((k) => (
                  <th key={k} className="att-team-dnum">{dayNum(k)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.userId}>
                  <td className="att-team-name">
                    {row.name}
                    <span className="att-muted" style={{ marginLeft: 6, fontSize: 11 }}>
                      {row.summary.present}p
                      {row.summary.leave > 0 && ` · ${row.summary.leave}l`}
                      {row.summary.absent > 0 && ` · ${row.summary.absent}a`}
                    </span>
                  </td>
                  {row.days.map((d) => (
                    <td key={d.dateKey}>
                      {/* A span, not a button: nothing here is clickable, and a
                          31×40 grid of buttons is 1,240 tab stops between the
                          name column and anything useful. */}
                      <span
                        className={`att-team-cell ${TONE[d.status] || 'is-upcoming'}${
                          d.dateKey === tk ? ' is-today' : ''}`}
                        title={describe(row, d)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="att-cal-legend">
        <span><i className="att-key is-present" /> Present</span>
        <span><i className="att-key is-half" /> Half day</span>
        <span><i className="att-key is-absent" /> Absent</span>
        <span><i className="att-key is-leave" /> Leave</span>
        <span><i className="att-key is-pending" /> Awaiting approval</span>
        <span><i className="att-key is-holiday" /> Holiday</span>
        <span><i className="att-key is-off" /> Weekly off</span>
      </div>
    </Card>
  );
}

export default TeamCalendar;
